import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { getBookById, updateBook } from "../services/booksService";
import { paginateHtmlContent } from "../services/manuscriptService";
import Stepper from "../components/Stepper";
import { auth } from "../firebase/firebaseConfig";
import { signOut } from "firebase/auth";
import DocxEditorModal from "../components/DocxEditorModal"; // This is our WordEditor component

// Six page-border / decorative template options, mirroring the styles
// supported inside WordEditor (PAGE_BORDER_STYLES). Each has a tiny inline
// CSS preview so the user can see the look before applying it.
const BORDER_TEMPLATES = [
    {
        id: "none",
        label: "No Border",
        preview: null,
    },
    {
        id: "classic_frame",
        label: "Classic Double Frame",
        preview: <div className="absolute inset-[3px] border border-gray-700" style={{ outline: "2px double #374151", outlineOffset: "-3px" }} />,
    },
    {
        id: "chevron_ribbon",
        label: "Chevron & Ribbon",
        preview: (
            <>
                <div className="absolute top-0 left-0 right-0 h-[10px]" style={{ background: "linear-gradient(120deg,#e8821c 0%,#e8821c 38%,#0e3a5c 38%,#0e3a5c 100%)", clipPath: "polygon(0 0,100% 0,100% 55%,0 100%)" }} />
                <div className="absolute bottom-0 left-0 right-0 h-[8px]" style={{ background: "linear-gradient(90deg,#0e3a5c,#1e6e4a)" }} />
            </>
        ),
    },
    {
        id: "mint_corners",
        label: "Mint Triangle Corners",
        preview: (
            <>
                <div className="absolute top-0 right-0 w-0 h-0" style={{ borderTop: "18px solid #bfe3cf", borderLeft: "18px solid transparent" }} />
                <div className="absolute top-0 right-0 w-[3px] h-full" style={{ background: "#bfe3cf" }} />
                <div className="absolute bottom-0 left-0 w-0 h-0" style={{ borderBottom: "18px solid #bfe3cf", borderRight: "18px solid transparent" }} />
            </>
        ),
    },
    {
        id: "stepped_corner",
        label: "Stepped Corner Bars",
        preview: (
            <>
                <div className="absolute top-0 left-0" style={{ width: "20px", height: "20px", background: "linear-gradient(135deg,#a9d6bd 0%,#a9d6bd 50%,transparent 50%)" }} />
                <div className="absolute bottom-0 left-0" style={{ width: "16px", height: "16px", background: "linear-gradient(45deg,#a9d6bd 0%,#a9d6bd 50%,transparent 50%)" }} />
            </>
        ),
    },
    {
        id: "olive_band",
        label: "Olive Chapter Band",
        preview: <div className="absolute top-0 left-0 right-0 h-[14px]" style={{ background: "#3c4a32" }} />,
    },
    {
        id: "sidebar_rule",
        label: "Rule & Sidebar Accent",
        preview: (
            <>
                <div className="absolute top-0 bottom-0 left-0 w-[3px]" style={{ background: "#c0394b" }} />
                <div className="absolute top-[10px] left-[6px] right-1 border-b border-gray-300" />
            </>
        ),
    },
];

const UploadManuscript = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const bookId = new URLSearchParams(location.search).get("bookId");

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [book, setBook] = useState(null);
    const [user, setUser] = useState(null);

    // Initial HTML content for the Word Editor
    const [initialWordHtml, setInitialWordHtml] = useState("");

    // Document-wide styling options (saved as docSettings)
    const [pageSize, setPageSize] = useState("trade");
    const [margins, setMargins] = useState("normal");
    const [theme, setTheme] = useState("cream");
    const [pageBorder, setPageBorder] = useState("none"); // "none", "single", "double"
    const [headerFooterEnabled, setHeaderFooterEnabled] = useState(true);
    const [defaultFontFamily, setDefaultFontFamily] = useState("serif");
    const [defaultFontSize, setDefaultFontSize] = useState(11);
    const [defaultAlignment, setDefaultAlignment] = useState("justify");
    const [headerText, setHeaderText] = useState("");
    const [footerText, setFooterText] = useState("");

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((u) => {
            if (u) {
                setUser(u);
            } else {
                navigate("/");
            }
        });
        return () => unsubscribe();
    }, [navigate]);

    // Load book data and set up initial HTML content
    useEffect(() => {
        const loadBook = async () => {
            if (!bookId) return;
            try {
                setLoading(true);
                const bookData = await getBookById(bookId);
                setBook(bookData);

                const typeToSize = {
                    pocket_book: "pocket_book",
                    small_paperback: "pocket",
                    digest_size: "digest",
                    trade_paperback: "trade",
                    large_book: "large_book",
                    childrens_book: "childrens_book",
                    square_book: "square_book",
                    textbook: "letter",
                    a5: "a5",
                    a4: "a4",
                };
                const defaultSizeFromType = typeToSize[bookData.bookType] || "trade";
                setPageSize(defaultSizeFromType);

                // Sensible default header/footer text derived from book metadata
                setHeaderText(bookData.title ? bookData.title : "");
                setFooterText(bookData.author ? `${bookData.author}` : "");

                if (bookData.manuscriptContent) {
                    try {
                        const parsed = JSON.parse(bookData.manuscriptContent);
                        if (parsed && parsed.pages) {
                            const combined = parsed.pages.map(p => p.content || "").join("");
                            setInitialWordHtml(combined);
                            if (parsed.docSettings) {
                                setPageSize(parsed.docSettings.pageSize || defaultSizeFromType);
                                setMargins(parsed.docSettings.margins || "normal");
                                setTheme(parsed.docSettings.theme || "cream");
                                setPageBorder(parsed.docSettings.pageBorder || "none");
                                setHeaderFooterEnabled(parsed.docSettings.headerFooterEnabled !== false);
                                setDefaultFontFamily(parsed.docSettings.defaultFontFamily || "serif");
                                setDefaultFontSize(parsed.docSettings.defaultFontSize || 11);
                                setDefaultAlignment(parsed.docSettings.defaultAlignment || "justify");
                                setHeaderText(parsed.docSettings.headerText ?? (bookData.title || ""));
                                setFooterText(parsed.docSettings.footerText ?? (bookData.author || ""));
                            }
                        } else {
                            setInitialWordHtml(bookData.manuscriptContent);
                        }
                    } catch (e) {
                        // If it's plain text, wrap lines in paragraphs
                        const htmlContent = bookData.manuscriptContent
                            .split(/\r?\n/)
                            .map(line => `<p>${line || "<br>"}</p>`)
                            .join("");
                        setInitialWordHtml(htmlContent);
                    }
                } else {
                    setInitialWordHtml(`<p style="font-family:Arial;font-size:12pt;">Start typing your document here...</p>`);
                }
            } catch (error) {
                console.error("Error loading book:", error);
            } finally {
                setLoading(false);
            }
        };

        if (bookId) {
            loadBook();
        }
    }, [bookId]);

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate("/");
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    // Save manuscript and settings, then navigate to preview
    const handleWordEditorSave = async (html, fileName) => {
        if (!bookId) return;
        try {
            setSaving(true);

            // Paginate the raw HTML content into page blocks using manuscriptService helper
            const paginatedPages = paginateHtmlContent(html);

            const docSettings = {
                pageSize,
                margins,
                theme,
                pageBorder,
                headerFooterEnabled,
                defaultFontFamily,
                defaultFontSize,
                defaultAlignment,
                headerText,
                footerText,
            };

            const payload = JSON.stringify({
                pages: paginatedPages,
                docSettings
            });

            await updateBook(bookId, {
                manuscriptContent: payload,
                pageSize,
                margins,
                theme,
                pageBorder,
                headerFooterEnabled,
                status: "manuscript-uploaded"
            });

            navigate(`/preview?bookId=${bookId}`);
        } catch (error) {
            console.error("Error saving manuscript:", error);
            alert("Failed to save manuscript: " + error.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#F8F7F4] to-[#F0EDE6] font-serif">
            {/* Top Navigation */}
            <nav className="bg-white px-6 md:px-10 flex items-center justify-between h-[72px] shadow-[0_2px_12px_rgba(0,0,0,0.08)] border-b border-[#F0F0F0]">
                <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 bg-[#DC2626] rounded-lg flex items-center justify-center text-lg">📚</div>
                    <span className="text-[#1E3A8A] font-extrabold text-xl tracking-tight">
                        Pub<span className="text-[#DC2626]">Tech</span>
                    </span>
                </div>

                <div className="hidden md:flex gap-8 items-center">
                    <Link to="/dashboard" className="text-gray-600 hover:text-[#1E3A8A] border-b-2 border-transparent text-sm font-medium transition-colors duration-150 no-underline py-2">Dashboard</Link>
                    <Link to="/create-book" className="text-gray-600 hover:text-[#1E3A8A] border-b-2 border-transparent text-sm font-medium transition-colors duration-150 no-underline py-2">Create Book</Link>
                    <Link to="/covers" className="text-gray-600 hover:text-[#1E3A8A] border-b-2 border-transparent text-sm font-medium transition-colors duration-150 no-underline py-2">Covers</Link>
                    <Link to="/upload" className="text-[#1E3A8A] border-b-2 border-[#DC2626] text-sm font-medium transition-colors duration-150 no-underline py-2">Upload</Link>
                    <Link to="/preview" className="text-gray-600 hover:text-[#1E3A8A] border-b-2 border-transparent text-sm font-medium transition-colors duration-150 no-underline py-2">Preview</Link>
                </div>

                <div className="flex items-center gap-4">
                    <span className="text-gray-500 text-xs font-semibold hidden sm:inline">{user?.email}</span>
                    <button onClick={handleLogout} className="px-4 py-2 bg-transparent hover:bg-[#DC2626] text-[#DC2626] hover:text-white border border-[#DC2626] rounded-lg text-xs font-semibold cursor-pointer transition-all duration-150">Log Out</button>
                </div>
            </nav>

            <div className="max-w-[1600px] mx-auto px-4 py-6">
                {!bookId ? (
                    <div className="max-w-3xl mx-auto text-center bg-white rounded-2xl shadow-xl p-12 border border-gray-100">
                        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <span className="text-4xl">📄</span>
                        </div>
                        <h2 className="text-2xl font-bold text-blue-900 mb-3">No active book selection</h2>
                        <p className="text-gray-600 mb-8">Please select a book first before editing your manuscript.</p>
                        <button
                            onClick={() => navigate("/dashboard")}
                            className="bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-900 text-white font-semibold px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                        >
                            Go to Dashboard
                        </button>
                    </div>
                ) : (
                    <div>
                        {/* Stepper progress */}
                        <div className="mb-4">
                            <Stepper currentStep={3} />
                        </div>

                        {/* Title Row */}
                        <div className="flex flex-wrap items-center justify-between gap-4 mb-4 bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                            <div className="flex items-center gap-3">
                                {book?.frontCover && (
                                    <img
                                        src={book.frontCover}
                                        alt="Book Cover"
                                        className="w-10 h-14 object-cover rounded-md shadow border border-gray-200"
                                    />
                                )}
                                <div>
                                    <h1 className="text-xl font-extrabold text-blue-900">
                                        Editing: {book?.title || "Book"}
                                    </h1>
                                    <p className="text-gray-500 text-xs font-semibold">
                                        by {book?.author} • {book?.genre}
                                    </p>
                                </div>
                            </div>
                            {saving && (
                                <div className="text-sm font-bold text-blue-950 animate-pulse">
                                    💾 Saving and processing manuscript layout...
                                </div>
                            )}
                        </div>

                        {/* WORKSTATION: Inline Word Editor & Settings Sidebar */}
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-[calc(100vh-230px)] min-h-[600px]">
                            {/* Editor Sheet Canvas */}
                            <div className="lg:col-span-9 bg-white rounded-2xl border border-gray-200 shadow-md overflow-hidden flex flex-col relative">
                                {loading ? (
                                    <div className="flex-1 flex flex-col items-center justify-center gap-3 bg-gray-50">
                                        <div className="w-10 h-10 border-4 border-blue-900 border-t-transparent rounded-full animate-spin"></div>
                                        <span className="text-sm text-blue-900 font-bold">Loading Manuscript Content...</span>
                                    </div>
                                ) : (
                                    <DocxEditorModal
                                        isOpen={true}
                                        isModal={false}
                                        initialHtml={initialWordHtml}
                                        onSave={handleWordEditorSave}
                                        saveButtonLabel="Save & Proceed"
                                        pageSize={pageSize}
                                        margins={margins}
                                        theme={theme}
                                        pageBorder={pageBorder}
                                        showHeaderFooter={headerFooterEnabled}
                                        headerText={headerText}
                                        footerText={footerText}
                                    />
                                )}
                            </div>

                            {/* Sidebar Document Settings */}
                            <div className="lg:col-span-3 bg-white p-6 rounded-2xl border border-gray-200 shadow-md flex flex-col justify-between overflow-y-auto">
                                <div className="space-y-6">
                                    <h3 className="text-md font-bold text-blue-900 border-b pb-3 flex items-center gap-2">
                                        <span>⚙️</span> Page Layout & Border
                                    </h3>

                                    {/* Page Size */}
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Book Page Size</label>
                                        <select
                                            value={pageSize}
                                            onChange={(e) => setPageSize(e.target.value)}
                                            className="w-full border border-gray-200 rounded-lg p-2.5 text-sm bg-white text-gray-700 focus:outline-none focus:border-blue-900"
                                        >
                                            <option value="pocket">Pocket Book (4.25" x 6.87")</option>
                                            <option value="digest">Digest Size (5.5" x 8.5")</option>
                                            <option value="trade">Trade Paperback (6.0" x 9.0")</option>
                                            <option value="letter">Letter Size (8.5" x 11.0")</option>
                                            <option value="a4">A4 Page (8.27" x 11.69")</option>
                                            <option value="a5">A5 Page (5.83" x 8.27")</option>
                                        </select>
                                    </div>

                                    {/* Margins */}
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Page Margins</label>
                                        <select
                                            value={margins}
                                            onChange={(e) => setMargins(e.target.value)}
                                            className="w-full border border-gray-200 rounded-lg p-2.5 text-sm bg-white text-gray-700 focus:outline-none focus:border-blue-900"
                                        >
                                            <option value="narrow">Narrow (0.5")</option>
                                            <option value="normal">Normal (0.8")</option>
                                            <option value="wide">Wide (1.2")</option>
                                        </select>
                                    </div>

                                    {/* Page Border / Decorative Template */}
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Page Border Style</label>
                                        <div className="grid grid-cols-2 gap-2">
                                            {BORDER_TEMPLATES.map((b) => (
                                                <button
                                                    key={b.id}
                                                    type="button"
                                                    onClick={() => setPageBorder(b.id)}
                                                    className={`relative flex flex-col items-center gap-1.5 rounded-lg border-2 p-2 transition-all duration-150 ${pageBorder === b.id ? "border-blue-900 bg-blue-50" : "border-gray-200 bg-white hover:border-gray-300"
                                                        }`}
                                                    title={b.label}
                                                >
                                                    {/* mini swatch preview */}
                                                    <div className="w-full h-10 rounded-md bg-white border border-gray-200 relative overflow-hidden">
                                                        {b.preview}
                                                    </div>
                                                    <span className="text-[9px] font-semibold text-gray-600 text-center leading-tight">{b.label}</span>
                                                    {pageBorder === b.id && (
                                                        <span className="absolute top-1 right-1 w-3.5 h-3.5 bg-blue-900 rounded-full flex items-center justify-center text-white text-[8px]">✓</span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Paper Theme */}
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Paper Color Style</label>
                                        <select
                                            value={theme}
                                            onChange={(e) => setTheme(e.target.value)}
                                            className="w-full border border-gray-200 rounded-lg p-2.5 text-sm bg-white text-gray-700 focus:outline-none focus:border-blue-900"
                                        >
                                            <option value="white">Standard White</option>
                                            <option value="cream">Premium Cream</option>
                                            <option value="sepia">Warm Sepia</option>
                                            <option value="charcoal">Dark Charcoal</option>
                                        </select>
                                    </div>

                                    {/* Running Header & Footers */}
                                    <div className="flex items-center justify-between pt-2">
                                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Headers & Page Numbers</span>
                                        <input
                                            type="checkbox"
                                            checked={headerFooterEnabled}
                                            onChange={(e) => setHeaderFooterEnabled(e.target.checked)}
                                            className="w-4 h-4 text-blue-900 border-gray-300 rounded focus:ring-blue-900"
                                        />
                                    </div>

                                    {headerFooterEnabled && (
                                        <div className="space-y-3 pt-1">
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Header Text</label>
                                                <input
                                                    value={headerText}
                                                    onChange={(e) => setHeaderText(e.target.value)}
                                                    className="w-full border border-gray-200 rounded-lg p-2.5 text-sm bg-white text-gray-700 focus:outline-none focus:border-blue-900"
                                                    placeholder="e.g. Book Title"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Footer Text</label>
                                                <input
                                                    value={footerText}
                                                    onChange={(e) => setFooterText(e.target.value)}
                                                    className="w-full border border-gray-200 rounded-lg p-2.5 text-sm bg-white text-gray-700 focus:outline-none focus:border-blue-900"
                                                    placeholder="e.g. Author Name"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="pt-4 border-t border-gray-150">
                                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-150">
                                        <p className="text-[11px] text-blue-800 leading-relaxed font-medium">
                                            💡 <strong>Tip:</strong> Use the <strong>Styles</strong> dropdown for headings, <strong>Insert → Break</strong> for manual page breaks, and <strong>Ctrl+F</strong> for Find & Replace.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UploadManuscript;